/**
 * Kontakter — the mosque's directory of the people it works with: imams, reciters, naat-khwans, speakers, scholars,
 * technicians, suppliers, authorities, politicians, the press, sponsors, volunteers, other mosques' boards.  MKK module 13
 * «Kontakter» (B1, 2026-10-24), made manageable 2026-10-31 (074, meeting notes 03). Staff only — except a contact ticked
 * «Vis i appen» (071): its name, role, phone and e-mail are then on the app's «Kontakt» page.
 *
 * Behind the `contacts_directory` capability (the menu item is absent without it, and every route answers 404).
 *   contacts.read     the list, search, category and tag chips, «Sist kontaktet», sort, copy a number, a contact's card;
 *                     «Fellesregister» (search the platform's shared register);
 *   contacts.manage   «Ny kontakt», «Endre», «Arkiver» (two clicks), «Hent tilbake», «Eksporter CSV» (no notes),
 *                     «Legg til i mine kontakter» from the shared register, and — while `contact_log` («Kontaktlogg») is
 *                     on — «Logg kontakt» and «Inviter til arrangement» (a touch with the event).
 * The server says which in every answer (`can.manage`) and decides again on each request. The touch history and the
 * logging buttons are hidden while the mosque does not have `contact_log` (GET /mosque capabilities, as the panel reads
 * them); their routes answer 404 then anyway.
 *
 * «Fellesregister» (2026-10-31): persons who registered themselves at the platform with Vipps and their consent. The tab
 * is there while the register is on (the list says `directory`). A copy is an ordinary contact marked «Fra
 * fellesregisteret»; if the person withdraws, the server clears the copied details at the next read and the row says
 * «Samtykke trukket» (name and category kept). Results and errors are toasts (useMsg).
 *
 * «Inviter en ressursperson» (2026-11-01, contacts.manage): a name and a phone or an e-mail; the platform sends ONE
 * message with a consent link (SMS only when the platform has an SMS sender — otherwise «SMS er ikke satt opp» and the
 * link is shown once to copy). The person is registered only if they consent. The mosque's own invitations are listed
 * below: name, channel, the contact masked by the server, sent, status; «Send på nytt» (once, after three days) and
 * «Trekk tilbake». Shown only while the server says invitations are on (`invites` in the list answer).
 */
import React, { useEffect, useState } from 'react';
import { API, can as canDo, call, download, errSummary, errText, fieldErrors, patch, post, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { channelBlocked, defaultInviteChannel, type InviteChannels } from '../lib/format';
import { useNav } from '../lib/nav';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Contacts, TouchLog } from '../core';

const CAT = Contacts.CATEGORY_NO;
const EMPTY = { name: '', category: 'imam', organisation: '', roleTitle: '', phone: '', email: '', languages: '', notes: '', tags: '', showInApp: false };
type Form = typeof EMPTY;
const toForm = (c: Body): Form => ({ name: c.name || '', category: c.category || 'other', organisation: c.organisation || '', roleTitle: c.roleTitle || '',
  phone: c.phone || '', email: c.email || '', languages: c.languages || '', notes: c.notes || '', tags: (c.tags || []).join(', '), showInApp: c.showInApp === true });
/** What is sent: empty fields are cleared (null), tags as typed — core splits and lower-cases them. */
const toBody = (f: Form) => ({ name: f.name, category: f.category, organisation: f.organisation || null, roleTitle: f.roleTitle || null,
  phone: f.phone || null, email: f.email || null, languages: f.languages || null, notes: f.notes || null, tags: f.tags, showInApp: f.showInApp });
/** +4741290118 → +47 412 90 118, for reading; the link keeps the number as stored. */
export function phoneText(p: string): string {
  const m = /^\+47(\d{3})(\d{2})(\d{3})$/.exec(p);
  return m ? `+47 ${m[1]} ${m[2]} ${m[3]}` : p;
}
/** An instant as a short Norwegian day («31.10.2026»), or «Aldri». */
export function dayText(iso: string | null | undefined): string {
  if (!iso) return 'Aldri';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Aldri' : d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
/** «Nå» for a datetime-local field, in the browser's own time. */
const localNow = () => { const d = new Date(); d.setSeconds(0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

function ContactForm({ f, set, errors }: { f: Form; set: (f: Form) => void; errors: Record<string, string> }) {
  const err = (k: string) => (errors[k] ? <span className="msg err" role="alert">{errors[k]}</span> : null);
  return (
    <>
      <div className="row">
        <Field label="Navn" required className="field half">{(id) => <><input id={id} maxLength={Contacts.MAX_NAME} autoComplete="off" value={f.name}
          onChange={(e) => set({ ...f, name: e.target.value })} />{err('name')}</>}</Field>
        <Field label="Kategori" required className="field half">{(id) => <><select id={id} value={f.category} onChange={(e) => set({ ...f, category: e.target.value })}>
          {Contacts.CATEGORIES.map((c) => <option key={c} value={c}>{CAT[c]}</option>)}</select>{err('category')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Rolle" className="field half" note="For eksempel «Gjesteimam» eller «Lydtekniker».">{(id, d) => <><input id={id} aria-describedby={d} maxLength={Contacts.MAX_ROLE}
          autoComplete="off" value={f.roleTitle} onChange={(e) => set({ ...f, roleTitle: e.target.value })} />{err('roleTitle')}</>}</Field>
        <Field label="Organisasjon" className="field half">{(id) => <><input id={id} maxLength={Contacts.MAX_ORGANISATION} autoComplete="off" value={f.organisation}
          onChange={(e) => set({ ...f, organisation: e.target.value })} />{err('organisation')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Telefon" className="field half" note="8 siffer, eller + og landkode.">{(id, d) => <><input id={id} aria-describedby={d} type="tel" inputMode="tel" maxLength={24}
          autoComplete="off" value={f.phone} onChange={(e) => set({ ...f, phone: e.target.value })} />{err('phone')}</>}</Field>
        <Field label="E-post" className="field half">{(id) => <><input id={id} type="email" maxLength={Contacts.MAX_EMAIL} autoComplete="off" value={f.email}
          onChange={(e) => set({ ...f, email: e.target.value })} />{err('email')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Språk" className="field half" note="For eksempel «norsk, arabisk, urdu».">{(id, d) => <><input id={id} aria-describedby={d} maxLength={Contacts.MAX_LANGUAGES}
          autoComplete="off" value={f.languages} onChange={(e) => set({ ...f, languages: e.target.value })} />{err('languages')}</>}</Field>
        <Field label="Stikkord" className="field half" note="Skilt med komma, for eksempel «ramadan, eid».">{(id, d) => <><input id={id} aria-describedby={d} maxLength={Contacts.MAX_TAGS}
          autoComplete="off" value={f.tags} onChange={(e) => set({ ...f, tags: e.target.value })} />{err('tags')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Notater" className="field" note="Bare for de ansatte. Vises aldri for medlemmer, og er aldri med i eksporten.">{(id, d) => <><textarea id={id} aria-describedby={d} rows={4}
          maxLength={Contacts.MAX_NOTES} value={f.notes} onChange={(e) => set({ ...f, notes: e.target.value })} />{err('notes')}</>}</Field>
      </div>
      <div className="row">
        <div className="field">
          <label className="work-check"><input type="checkbox" checked={f.showInApp} aria-describedby="contact-in-app-note"
            onChange={(e) => set({ ...f, showInApp: e.target.checked })} /> Vis i appen (under «Kontakt»)</label>
          <p id="contact-in-app-note" className={f.showInApp ? 'msg warn' : 'note'} role="note">Synlig for alle som bruker appen. Bare med personens samtykke.
            {' '}Navn, rolle, telefon og e-post vises — aldri organisasjon, språk, stikkord eller notater.</p>
          {err('showInApp')}
        </div>
      </div>
    </>
  );
}

/** Copy one value (a phone, an e-mail) and say so in a toast. */
function CopyValue({ value, what, say }: { value: string; what: string; say: (t: string, tone?: 'ok' | 'err' | 'warn' | '') => void }) {
  return <Btn ghost aria-label={`Kopier ${what}`} title={`Kopier ${what}`} onClick={async () => {
    try { await navigator.clipboard.writeText(value); say(`${what[0].toUpperCase()}${what.slice(1)} er kopiert.`, 'ok'); }
    catch { say('Nettleseren lot oss ikke kopiere. Merk teksten og trykk Ctrl+C (Cmd+C på Mac).', 'warn'); }
  }}>Kopier</Btn>;
}

type Touch = { contact: Body; invite: boolean } | null;

/** «Logg kontakt» / «Inviter til arrangement»: one touch on the foundation's route (POST /touches, kind contact). */
function TouchDialog({ open, onClose, onSaved }: { open: Touch; onClose: () => void; onSaved: (c: Body) => void }) {
  const [channel, setChannel] = useState('phone');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState(localNow());
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState<{ id: string; title: string; startsAt: string }[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [msg, say] = useMsg();
  useEffect(() => {
    if (!open) return;
    setChannel('phone'); setNote(''); setWhen(localNow()); setEventId(''); setErrors({}); setEvents(null);
    // The events to choose from: what has not started yet. Staff who write events see drafts too; others the published ones.
    const now = Date.now();
    const pick = (rows: Body[]) => rows.map((e) => ({ id: e.id, title: e.title || e.title_no || '', startsAt: e.startsAt || e.starts_at || '' }))
      .filter((e) => e.id && e.startsAt && Date.parse(e.startsAt) >= now).sort((a, b) => a.startsAt.localeCompare(b.startsAt)).slice(0, 50);
    (async () => {
      try {
        if (canDo('events.write')) {
          const r = await call('/events');
          setEvents(pick(((r.items || []) as Body[]).filter((e) => !['cancelled', 'archived'].includes(e.status))));
        } else {
          const r = await fetch(`${API}/events/upcoming?limit=50`).then((x) => (x.ok ? x.json() : { items: [] }));
          setEvents(pick(r.items || []));
        }
      } catch { setEvents([]); }
    })();
  }, [open]);
  if (!open) return null;
  const c = open.contact;
  const save = async () => {
    if (saving) return;
    if (open.invite && !eventId) { setErrors({ eventId: 'Velg arrangementet.' }); return say('Velg arrangementet.', 'err'); }
    setSaving(true);
    try {
      const at = when ? new Date(when) : null;
      const r = await post('/touches', { kind: 'contact', id: c.id, channel, note: note.trim() || null,
        touchedAt: at && !Number.isNaN(at.getTime()) ? at.toISOString() : null, eventId: eventId || null });
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      say(open.invite ? `Invitasjonen til ${c.name} er logget.` : `Kontakten med ${c.name} er logget.`, 'ok');
      onSaved(c);
    } finally { setSaving(false); }
  };
  return (
    <FormDialog open title={open.invite ? `Inviter ${c.name} til et arrangement` : `Logg kontakt med ${c.name}`} dirty={Boolean(note)} onClose={onClose}>
      <div className="row">
        <Field label="Hvordan" required className="field half">{(id) => <select id={id} value={channel} onChange={(e) => setChannel(e.target.value)}>
          {TouchLog.CHANNELS.map((k) => <option key={k} value={k}>{TouchLog.CHANNEL_NO[k]}</option>)}</select>}</Field>
        <Field label="Når" className="field half" error={errors.touchedAt}>{(id) => <input id={id} type="datetime-local" value={when} max={localNow()}
          onChange={(e) => setWhen(e.target.value)} />}</Field>
      </div>
      <div className="row">
        <Field label={open.invite ? 'Arrangement' : 'Arrangement (valgfritt)'} required={open.invite} className="field" error={errors.eventId}>{(id) => (
          events === null ? <span className="note">Henter arrangementer …</span>
            : <select id={id} value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">{events.length ? '— Ingen —' : 'Ingen kommende arrangementer'}</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.title} · {dayText(e.startsAt)}</option>)}</select>)}</Field>
      </div>
      <div className="row">
        <Field label="Notat (valgfritt)" className="field" note="Bare for de ansatte. Kort: hva ble sagt eller avtalt." error={errors.note}>{(id, d) => <textarea id={id}
          aria-describedby={d} rows={3} maxLength={TouchLog.MAX_NOTE} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      </div>
      <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : open.invite ? 'Logg invitasjonen' : 'Logg kontakt'}</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
    </FormDialog>
  );
}

/** A contact's card: what is known, and — while «Kontaktlogg» is on — every touch, newest first. */
function ContactCard({ contact, logOn, manage, onClose, onTouch, version }: {
  contact: Body | null; logOn: boolean; manage: boolean; onClose: () => void; onTouch: (c: Body, invite: boolean) => void; version: number;
}) {
  const { data, error, reload } = useLoad<Body>(contact && logOn ? `/touches?kind=contact&id=${encodeURIComponent(contact.id)}` : null, [contact && contact.id, logOn, version]);
  if (!contact) return null;
  const c = contact;
  const facts: [string, React.ReactNode][] = [['Kategori', CAT[c.category] || c.category], ['Rolle', c.roleTitle || '—'], ['Organisasjon', c.organisation || '—'],
    ['Telefon', c.phone ? <a href={`tel:${c.phone}`}>{phoneText(c.phone)}</a> : '—'], ['E-post', c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '—'],
    ['Språk', c.languages || '—'], ['Stikkord', (c.tags || []).join(', ') || '—'], ['Sist kontaktet', dayText(c.lastTouchedAt)]];
  const touches: Body[] = (data && data.items) || [];
  return (
    <FormDialog open title={c.name} wide onClose={onClose}>
      <table className="t"><tbody>{facts.map(([k, v]) => <tr key={k}><th scope="row">{k}</th><td>{v}</td></tr>)}</tbody></table>
      {c.notes && <CardBody><p className="note" style={{ whiteSpace: 'pre-line' }}>{c.notes}</p></CardBody>}
      {logOn && <>
        <h3>Kontaktlogg</h3>
        {manage && !c.archivedAt && <div className="work-bar">
          <Btn small={false} onClick={() => onTouch(c, false)}>Logg kontakt</Btn>
          <Btn ghost small={false} onClick={() => onTouch(c, true)}>Inviter til arrangement</Btn>
        </div>}
        {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : touches.length ? (
          <table className="t">
            <thead><tr><th scope="col">Når</th><th scope="col">Hvordan</th><th scope="col">Arrangement</th><th scope="col">Notat</th><th scope="col">Av</th></tr></thead>
            <tbody>{touches.map((t) => <tr key={t.id}><td>{dayText(t.touchedAt)}</td><td>{t.channelLabel || t.channel}</td><td>{t.eventTitle || '—'}</td>
              <td style={{ whiteSpace: 'pre-line' }}>{t.note || '—'}</td><td>{t.createdByName || '—'}</td></tr>)}</tbody>
          </table>) : <p className="note">Ingen kontakt logget ennå.</p>}
      </>}
    </FormDialog>
  );
}

const INVITE_STATUS: Record<string, [string, 'green' | 'grey' | 'gold']> = { sent: ['Sendt', 'gold'], accepted: ['Akseptert', 'green'], declined: ['Avslått', 'grey'],
  expired: ['Utløpt', 'grey'], withdrawn: ['Trukket tilbake', 'grey'], answered: ['Besvart', 'grey'] };
const INVITE_CHANNEL: Record<string, string> = { sms: 'SMS', email: 'E-post', link: 'Lenke (sendt selv)' };
const INVITE_EMPTY = { name: '', phone: '', email: '', channel: '', categories: [] as string[], note: '' };
type InviteForm = typeof INVITE_EMPTY;

/** «Inviter en ressursperson»: the form, then (for a link) the link once, to copy. */
function InviteDialog({ open, channels, onClose, onSent }: { open: boolean; channels: InviteChannels; onClose: () => void; onSent: () => void }) {
  const [f, setF] = useState<InviteForm>(INVITE_EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState('');
  const [msg, say] = useMsg();
  useEffect(() => { if (open) { setF({ ...INVITE_EMPTY, channel: defaultInviteChannel(channels) }); setErrors({}); setLink(''); } }, [open]);
  if (!open) return null;
  const blocked = channelBlocked(f.channel, channels);
  const send = async () => {
    if (saving) return;
    if (blocked) { setErrors({ channel: blocked }); return say(blocked, 'err'); }
    setSaving(true);
    try {
      const r = await post('/contacts/directory/invites', { name: f.name, phone: f.phone || null, email: f.email || null, channel: f.channel,
        categories: f.categories, note: f.note.trim() || null });
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      onSent();
      if (r.body.link) { setLink(r.body.link); return say('Invitasjonen er laget. Kopier lenken og send den selv — den vises bare nå.', 'ok'); }
      say(`Invitasjonen til ${f.name} er sendt.`, 'ok');
      onClose();
    } finally { setSaving(false); }
  };
  const toggle = (c: string) => setF({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] });
  return (
    <FormDialog open title="Inviter en ressursperson" dirty={!link && Boolean(f.name || f.phone || f.email || f.note)} onClose={onClose}
      hint="Personen får én melding med en lenke, og blir bare registrert hvis de selv logger inn med Vipps og samtykker. Telefonnummeret eller e-posten brukes bare til denne meldingen og slettes når invitasjonen er besvart; navnet senest etter 14 dager.">
      {link ? <>
        <div className="row"><Field label="Lenken" className="field" note="Send den på SMS, e-post eller WhatsApp. Den vises ikke igjen.">{(id, d) => <input id={id} aria-describedby={d} readOnly value={link}
          onFocus={(e) => e.currentTarget.select()} />}</Field></div>
        <div className="row"><CopyValue value={link} what="lenken" say={say} /> <Btn ghost small={false} onClick={onClose}>Ferdig</Btn></div>
      </> : <>
        <div className="row">
          <Field label="Navn" required className="field half" error={errors.name}>{(id) => <input id={id} maxLength={120} autoComplete="off" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field>
          <Field label="Send med" required className="field half" error={errors.channel} note={blocked || (f.channel === 'link' ? 'Du får lenken her, og sender den selv.' : undefined)}>{(id, d) => (
            <select id={id} aria-describedby={d} value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>
              <option value="sms">SMS{channels.sms ? '' : ' (ikke satt opp)'}</option>
              <option value="email">E-post{channels.email ? '' : ' (ikke satt opp)'}</option>
              <option value="link">Lenke jeg sender selv</option></select>)}</Field>
        </div>
        <div className="row">
          <Field label="Telefon" className="field half" note="8 siffer, eller + og landkode." error={errors.phone}>{(id, d) => <input id={id} aria-describedby={d} type="tel" inputMode="tel"
            maxLength={24} autoComplete="off" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />}</Field>
          <Field label="E-post" className="field half" error={errors.email}>{(id) => <input id={id} type="email" maxLength={254} autoComplete="off" value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })} />}</Field>
        </div>
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="note">Foreslåtte kategorier (valgfritt — personen velger selv)</legend>
          <div className="bnr-seg work-seg" role="group" aria-label="Foreslåtte kategorier">
            {Contacts.CATEGORIES.map((c) => <button key={c} type="button" aria-pressed={f.categories.includes(c)} onClick={() => toggle(c)}>{CAT[c]}</button>)}
          </div>
          {errors.categories && <span className="msg err" role="alert">{errors.categories}</span>}
        </fieldset>
        <div className="row">
          <Field label="Personlig hilsen (valgfritt)" className="field" note="Høyst 200 tegn. Står i meldingen." error={errors.note}>{(id, d) => <textarea id={id} aria-describedby={d}
            rows={2} maxLength={200} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />}</Field>
        </div>
        <div className="row"><Btn small={false} disabled={saving} onClick={send}>{saving ? 'Sender …' : f.channel === 'link' ? 'Lag lenken' : 'Send invitasjonen'}</Btn></div>
      </>}
      <Msg text={msg.text} tone={msg.tone} />
    </FormDialog>
  );
}

/** The mosque's own invitations: name, channel, the contact masked, sent, status; resend once, withdraw. */
function Invitations({ channels }: { channels: InviteChannels }) {
  const { data, error, reload } = useLoad<Body>('/contacts/directory/invites', []);
  const [open, setOpen] = useState(false);
  const [msg, say] = useMsg();
  const act = async (v: Body, what: 'resend' | 'withdraw') => {
    const r = await post(`/contacts/directory/invites/${v.id}/${what}`);
    say(r.ok ? (what === 'resend' ? 'Invitasjonen er sendt på nytt.' : 'Invitasjonen er trukket tilbake.') : errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  };
  const items: Body[] = (data && data.items) || [];
  return (
    <>
      <div className="work-bar">
        <h3 style={{ margin: 0 }}>Invitasjoner</h3>
        <Btn small={false} onClick={() => setOpen(true)}>+ Inviter en ressursperson</Btn>
      </div>
      <Msg text={msg.text} tone={msg.tone} />
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <table className="t">
          <thead><tr><th scope="col">Navn</th><th scope="col">Kanal</th><th scope="col">Sendt</th><th scope="col">Status</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.length ? items.map((v) => {
            const [label, tone] = INVITE_STATUS[v.status] || [v.status, 'grey'];
            return (
              <tr key={v.id}>
                <td><b>{v.name || '(navn slettet)'}</b>{v.contact && <div className="sub">{v.contact}</div>}</td>
                <td>{INVITE_CHANNEL[v.channel] || v.channel}</td>
                <td>{dayText(v.sentAt)}</td>
                <td><Tag tone={tone}>{label}</Tag></td>
                <td>{v.canResend && <><Btn ghost aria-label={`Send invitasjonen til ${v.name || 'personen'} på nytt`} onClick={() => act(v, 'resend')}>Send på nytt</Btn>{' '}</>}
                  {v.canWithdraw && <TwoStep label="Trekk tilbake" ariaLabel={`Trekk tilbake invitasjonen til ${v.name || 'personen'}`} busyLabel="Trekker tilbake …"
                    confirmLabel="Klikk igjen: lenken slutter å virke" onConfirm={() => act(v, 'withdraw')} />}</td>
              </tr>);
          }) : <tr><td colSpan={5}>Ingen invitasjoner ennå. Kjenner du en imam, foredragsholder eller frivillig? Inviter dem — de bestemmer selv.</td></tr>}</tbody>
        </table>
      )}
      <InviteDialog open={open} channels={channels} onClose={() => setOpen(false)} onSent={reload} />
    </>
  );
}

/** «Fellesregister»: persons who registered themselves at the platform, visible to this mosque. */
function SharedDirectory({ manage, onAdded, invites }: { manage: boolean; onAdded: () => void; invites: { channels: InviteChannels } | null }) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [cityTyped, setCityTyped] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, say] = useMsg();
  useEffect(() => { const t = setTimeout(() => { setQ(typed.trim()); setCity(cityTyped.trim()); }, 250); return () => clearTimeout(t); }, [typed, cityTyped]);
  const qs = new URLSearchParams({ ...(q ? { q } : {}), ...(category ? { category } : {}), ...(city ? { city } : {}) }).toString();
  const { data, error, reload } = useLoad<Body>('/contacts/directory' + (qs ? '?' + qs : ''), [qs]);
  const add = async (p: Body) => {
    setBusy(p.id);
    try {
      const r = await post(`/contacts/directory/${p.id}/add`);
      say(r.ok ? `${p.name} er lagt til i kontaktene.` : errText(r.body), r.ok ? 'ok' : 'err');
      if (r.ok) { reload(); onAdded(); }
    } finally { setBusy(''); }
  };
  const items: Body[] = (data && data.items) || [];
  return (
    <>
      <CardBody><p className="note">Personer som selv har registrert seg med Vipps og samtykket til at moskeene på plattformen kan se dem og kopiere dem til kontaktene sine.
        {' '}Telefon og e-post følger med når du legger personen til. Trekker personen samtykket, skjules de her, og kopien beholder bare navn og kategori.</p></CardBody>
      <div className="work-bar">
        <Field label="Søk" className="field work-search">{(id) => <input id={id} type="search" value={typed} onChange={(e) => setTyped(e.target.value)}
          placeholder="Navn, språk eller omtale" maxLength={Contacts.MAX_QUERY} />}</Field>
        <Field label="Sted" className="field">{(id) => <input id={id} type="search" value={cityTyped} onChange={(e) => setCityTyped(e.target.value)} maxLength={Contacts.MAX_QUERY} />}</Field>
        <Field label="Kategori" className="field">{(id) => <select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Alle</option>{Contacts.CATEGORIES.map((c) => <option key={c} value={c}>{CAT[c]}</option>)}</select>}</Field>
      </div>
      <Msg text={msg.text} tone={msg.tone} />
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <table className="t">
          <thead><tr><th scope="col">Navn</th><th scope="col">Kategorier</th><th scope="col">Sted</th><th scope="col">Språk</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.length ? items.map((p) => (
            <tr key={p.id}>
              <td><b>{p.name}</b>{p.bio && <div className="sub">{p.bio}</div>}</td>
              <td>{(p.categories || []).map((c: string) => CAT[c] || c).join(', ')}</td>
              <td>{p.city || '—'}</td>
              <td>{p.languages || '—'}</td>
              <td>{p.added ? <Tag tone="green">Lagt til</Tag> : manage && <Btn ghost disabled={busy === p.id} aria-label={`Legg ${p.name} til i mine kontakter`}
                onClick={() => add(p)}>{busy === p.id ? 'Legger til …' : 'Legg til i mine kontakter'}</Btn>}</td>
            </tr>)) : <tr><td colSpan={5}>{q || category || city ? 'Ingen i fellesregisteret passer søket.' : 'Ingen har registrert seg for denne moskeen ennå.'}</td></tr>}</tbody>
        </table>
      )}
      {manage && invites && <Invitations channels={invites.channels} />}
    </>
  );
}

export function Kontakter() {
  const { caps } = useNav();
  const logOn = Boolean(caps && caps.includes('contact_log'));
  const [tab, setTab] = useState<'list' | 'directory'>('list');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [touched, setTouched] = useState('');
  const [sort, setSort] = useState('name');
  const [archived, setArchived] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(typed.trim()), 250); return () => clearTimeout(t); }, [typed]);
  const qs = new URLSearchParams({ archived: archived ? 'all' : 'no', ...(category ? { category } : {}), ...(q ? { q } : {}),
    ...(tags.length ? { tags: tags.join(',') } : {}), ...(touched ? { touched } : {}), ...(sort !== 'name' ? { sort } : {}) }).toString();
  const { data, error, reload } = useLoad<Body>('/contacts?' + qs, [qs]);
  const [msg, say] = useMsg();
  const [form, setForm] = useState<{ id: string | null; f: Form; start: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [card, setCard] = useState<Body | null>(null);
  const [touch, setTouch] = useState<Touch>(null);
  const [touchVersion, setTouchVersion] = useState(0);
  const [exporting, setExporting] = useState(false);

  const open = (id: string | null, f: Form) => { setErrors({}); say(''); setForm({ id, f, start: f }); };
  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    try {
      const r = form.id ? await patch(`/contacts/${form.id}`, toBody(form.f)) : await post('/contacts', toBody(form.f));
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setForm(null); say(form.id ? 'Kontakten er lagret.' : 'Kontakten er lagt til.', 'ok'); reload();
    } finally { setSaving(false); }
  };
  const act = async (c: Body, what: 'archive' | 'restore') => {
    const r = await post(`/contacts/${c.id}/${what}`);
    say(r.ok ? (what === 'archive' ? `${c.name} er arkivert.` : `${c.name} er hentet tilbake.`) : errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const d = await download(`/v1/admin/contacts/export.csv?${qs}`, `kontakter-${(data && data.today) || 'liste'}.csv`);
      say(d.ok ? 'Listen er lastet ned (uten notater).' : d.message || 'Eksporten gikk ikke.', d.ok ? 'ok' : 'err');
    } finally { setExporting(false); }
  };
  const toggleTag = (t: string) => setTags(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);

  const items: Body[] = (data && data.items) || [];
  const allTags: string[] = (data && data.tags) || [];
  const manage = Boolean(data && data.can && data.can.manage);
  const directoryOn = Boolean(data && data.directory);
  const filtered = Boolean(q || category || tags.length || touched);
  return (
    <Card title="Kontakter" hint="Personer moskeen samarbeider med: imamer, resitatorer, naat-khwans, foredragsholdere, lærde, teknikere, leverandører og flere. Bare for ansatte — unntatt kontakter merket «Vis i appen», som vises under «Kontakt» i appen.">
      {directoryOn && <div className="work-bar">
        <div className="bnr-seg work-seg" role="group" aria-label="Kontakter: velg del">
          <button type="button" aria-pressed={tab === 'list'} onClick={() => setTab('list')}>Mine kontakter</button>
          <button type="button" aria-pressed={tab === 'directory'} onClick={() => setTab('directory')}>Fellesregister</button>
        </div>
      </div>}
      {tab === 'directory' && directoryOn ? <SharedDirectory manage={manage} onAdded={reload} invites={(data && data.invites) || null} /> : <>
        <div className="work-bar">
          <Field label="Søk" className="field work-search">{(id) => (
            <input id={id} type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Navn, organisasjon, rolle eller stikkord" maxLength={Contacts.MAX_QUERY} />
          )}</Field>
          <Field label="Sist kontaktet" className="field">{(id) => <select id={id} value={touched} onChange={(e) => setTouched(e.target.value)}>
            <option value="">Alle</option>{Contacts.TOUCHED.map((k) => <option key={k} value={k}>{Contacts.TOUCHED_NO[k]}</option>)}</select>}</Field>
          <Field label="Sorter" className="field">{(id) => <select id={id} value={sort} onChange={(e) => setSort(e.target.value)}>
            {Contacts.SORTS.map((k) => <option key={k} value={k}>{Contacts.SORT_NO[k]}</option>)}</select>}</Field>
          <label className="field note" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Vis arkiverte
          </label>
          {manage && <Btn ghost small={false} disabled={exporting} onClick={exportCsv}>{exporting ? 'Lager fil …' : 'Eksporter CSV'}</Btn>}
          {manage && <Btn small={false} onClick={() => open(null, EMPTY)}>+ Ny kontakt</Btn>}
        </div>
        <div className="work-bar">
          <div className="bnr-seg work-seg" role="group" aria-label="Kategori">
            <button type="button" aria-pressed={category === ''} onClick={() => setCategory('')}>Alle</button>
            {Contacts.CATEGORIES.map((c) => <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)}>{CAT[c]}</button>)}
          </div>
        </div>
        {allTags.length > 0 && <div className="work-bar">
          <div className="bnr-seg work-seg" role="group" aria-label="Stikkord">
            {allTags.map((t) => <button key={t} type="button" aria-pressed={tags.includes(t)} onClick={() => toggleTag(t)}>#{t}</button>)}
          </div>
        </div>}
        {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
        {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
          <>
            <p className="vh" role="status">{items.length === 1 ? '1 kontakt' : `${items.length} kontakter`}</p>
            <table className="t">
              <thead><tr><th scope="col">Navn</th><th scope="col">Kategori</th><th scope="col">Telefon</th><th scope="col">E-post</th><th scope="col">Sist kontaktet</th>
                <th scope="col"><span className="vh">Handling</span></th></tr></thead>
              <tbody>{items.length ? items.map((c) => (
                <tr key={c.id} style={c.directoryPersonId && !c.consentShared ? { opacity: 0.7 } : undefined}>
                  <td><button type="button" className="link" onClick={() => setCard(c)}><b>{c.name}</b></button>
                    {c.archivedAt && <> <Tag tone="grey">Arkivert</Tag></>}
                    {c.showInApp && !c.archivedAt && <> <Tag tone="green" title="Vises på «Kontakt» i appen">I appen</Tag></>}
                    {c.directoryPersonId && (c.consentShared ? <> <Tag tone="gold" title="Kopiert fra fellesregisteret, med personens samtykke">Fra fellesregisteret</Tag></>
                      : <> <Tag tone="grey" title="Personen har trukket samtykket: bare navn og kategori er beholdt">Samtykke trukket</Tag></>)}
                    {(c.roleTitle || c.organisation) && <div className="sub">{[c.roleTitle, c.organisation].filter(Boolean).join(' · ')}</div>}
                    {c.tags && c.tags.length > 0 && <div className="sub">{c.tags.map((t: string) => `#${t}`).join(' ')}</div>}</td>
                  <td>{CAT[c.category] || c.category}</td>
                  <td>{c.phone ? <><a href={`tel:${c.phone}`}>{phoneText(c.phone)}</a> <CopyValue value={c.phone} what="telefonnummeret" say={say} /></> : '—'}</td>
                  <td>{c.email ? <><a href={`mailto:${c.email}`}>{c.email}</a> <CopyValue value={c.email} what="e-postadressen" say={say} /></> : '—'}</td>
                  <td>{dayText(c.lastTouchedAt)}</td>
                  <td>{manage && (c.archivedAt
                    ? <Btn ghost aria-label={`Hent tilbake ${c.name}`} onClick={() => act(c, 'restore')}>Hent tilbake</Btn>
                    : <>{logOn && <><Btn ghost aria-label={`Logg kontakt med ${c.name}`} onClick={() => setTouch({ contact: c, invite: false })}>Logg kontakt</Btn>{' '}</>}
                      <Btn ghost aria-label={`Endre ${c.name}`} onClick={() => open(c.id, toForm(c))}>Endre</Btn>{' '}
                      <TwoStep label="Arkiver" ariaLabel={`Arkiver ${c.name}`} busyLabel="Arkiverer …"
                        confirmLabel="Klikk igjen: kontakten skjules fra listen" onConfirm={() => act(c, 'archive')} /></>)}</td>
                </tr>)) : <tr><td colSpan={6}>{filtered ? 'Ingen kontakter passer søket.' : manage ? 'Ingen kontakter ennå. Legg til den første med «Ny kontakt».' : 'Ingen kontakter ennå.'}</td></tr>}</tbody>
            </table>
            {!archived && data.archivedCount > 0 && <CardBody><p className="note">{data.archivedCount === 1 ? '1 arkivert kontakt er skjult.' : `${data.archivedCount} arkiverte kontakter er skjult.`}</p></CardBody>}
          </>
        )}
      </>}
      <FormDialog open={Boolean(form)} title={form && form.id ? 'Endre kontakt' : 'Ny kontakt'} dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify(form!.start)}
        onClose={() => setForm(null)}>
        {form && <>
          <ContactForm f={form.f} set={(f) => { setErrors(dropChanged(errors, form.f, f)); setForm({ ...form, f }); }} errors={errors} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : form.id ? 'Lagre' : 'Legg til'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
      <ContactCard contact={card && (items.find((x) => x.id === card.id) || card)} logOn={logOn} manage={manage} version={touchVersion} onClose={() => setCard(null)}
        onTouch={(c, invite) => setTouch({ contact: c, invite })} />
      <TouchDialog open={touch} onClose={() => setTouch(null)} onSaved={() => { setTouch(null); setTouchVersion((v) => v + 1); reload(); }} />
    </Card>
  );
}
